import { useState, useEffect, VFC } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosResponse } from "axios";
import { Grid, Box, Button, SelectChangeEvent, Typography } from "@mui/material";

import { FormText } from "../common/form/FormText";
import { PullDownItem, FormPullDown } from "../common/form/FormPullDown";
import ButtonArea from "../common/ButtonArea";
import { ResultResponse, ResultDialog } from "../common/dialog/ResultDialog";
import { ContractService } from "./api/ContractService";
import { ContractSearchRequest, ContractSearchResponse } from "./api/ContractForm";
import Constants from "../../utils/Constants";
import { Utility } from "../../utils/Utility";
import { ApplicationProtectedRouting } from "../../Routing";
import { ContractTable } from "../common/table/ContractTable";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { ContractEditMode } from "../../entity/ContractEditMode";
import ContractSearchDateType, { ContractSearchDateTypeConverter, ContractSearchDateTypeKey } from "../../entity/ContractSearchDateType";
import { Loading } from "../common/Loading";

type Props = { 
  isSearch?: boolean,
  preContractList?: ContractSearchResponse[],
}

/**
 * 契約一覧画面
 */
const ContractList: VFC<Props> = ({ 
  isSearch = true,
  preContractList, 
}) => {
  const navigate = useNavigate();
  const PAGE_NAME = "契約一覧";
  const WIDTH = 2;
  const searchDateOptions = Object.entries(ContractSearchDateType).map(([key, value]) => {
    return {key: key, value: value} as PullDownItem
  });

  /***************************************************************
   * State管理
   **************************************************************/
  // 共通ローディング
  const [isLoading, setIsLoading] = useState(false);
  
  // 検索条件
  const [request, setRequest] = useState<ContractSearchRequest>(
    new ContractSearchRequest()
  );

  // 検索期間のタイプ
  const [searchDateOption, setSearchDateOption] = useState<ContractSearchDateTypeKey>("onContract");

  // 契約一覧
  const [contractList, setContractList] = useState([] as ContractSearchResponse[]);

  // 共通ダイアログ(ステータス)
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    setIsDialogOpen(false);
  };
  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    setIsDialogOpen(true);
    setResultResponse({
      status,
      code,
      displayMessage,
      result: [result],
      errorMessages: [],
    });
  };

  /***************************************************************
   * 初期表示処理
   **************************************************************/
  useEffect(() => {
    // セッション破棄
    sessionStorage.removeItem("CONTRACT_ID");

    // 検索処理
    preContractList == null ? searchButtonClick() : setContractList(preContractList);
  }, []);

  /***************************************************************
   * 検索処理
   **************************************************************/
  // 入力変更
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRequest({ ...request, [e.target.name]: e.target.value });
  };
  const handleSelectChange = (e: SelectChangeEvent) => {
    setRequest({ ...request, [e.target.name]: e.target.value });
  };
  const handleDateOptionChange = (e: SelectChangeEvent) => {
    const dateOption = e.target.value as ContractSearchDateTypeKey;
    setSearchDateOption(dateOption);
    request.searchDateMode = ContractSearchDateTypeConverter(dateOption);
  }
  const handleSearchDateChange = (searchDate: Date | null) => {
    request.searchDate = searchDate !== null ? searchDate.toLocaleString() : "";
  }
  // 検索実行(Enter)
  const handleInputSearch = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      searchButtonClick();
    }
  };

  // 検索ボタン押下
  const searchButtonClick = () => {
    setIsLoading(true);
    // 契約一覧取得API;
    const service = new ContractService();
    service
      .findByConditions(request)
      .then((response: AxiosResponse) => {
        if (Constants.ERR_STATUS_OK === response.data.status) {
          // OK
          setContractList(response.data.data);
        } else {
          setIsDialogOpen(true);
          setResultResponse(response.data);
        }
      })
      .catch((err) => {
        setStateResultResponse(
          Constants.ERR_STATUS_NG,
          err.response.status,
          err.response.statusText,
          Constants.E_MSG_SYSTEM_ERR
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /***************************************************************
   * 新規追加
   **************************************************************/
  // 新規追加ボタン押下
  const newButtonClick = () => {
    // ユーザー編集/新規作成画面へ遷移する
    navigate(ApplicationProtectedRouting.contractCreate.path(), {state: {
      mode: ContractEditMode.CREATE
    }});
  };

  /***************************************************************
   * レイアウト
   **************************************************************/

  // 検索フォーム
  const SearchForm = () => {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* 検索条件：公開コード */}
          <FormText
            label="契約コード"
            name="disclosureCode"
            xs={WIDTH}
            handle={handleInputChange}
            handleKeyDown={handleInputSearch}
          />

          {/* 検索条件：テナント名 */}
          <FormText
            label="テナント名"
            name="tenantName"
            xs={WIDTH}
            handle={handleInputChange}
            handleKeyDown={handleInputSearch}
          />

          {/* 検索条件：企業名 */}
          <FormText
            label="企業名"
            name="companyName"
            xs={WIDTH}
            handle={handleInputChange}
            handleKeyDown={handleInputSearch}
          />

          {/* 検索条件：組織名 */}
          {/* <FormText
            label="組織名"
            name="organizationName"
            xs={WIDTH}
            handle={handleInputChange}
          /> */}

          {/* 日付の検索条件オプション */}
          <FormPullDown
            label="日付検索タイプ"
            name="dateOption"
            items={searchDateOptions}
            xs={WIDTH}
            value={searchDateOption}
            handle={handleDateOptionChange}
          />

          {/* 検索条件：日付 */}
          <Grid 
            item 
            xs={WIDTH}
          >
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ja}
              dateFormats={{ monthAndYear: "yyyy年 MM月" }}
              localeText={{
                previousMonth: "前月を表示", // < のツールチップ
                nextMonth: "次月を表示", // > のツールチップ
              }}
            >
              <DatePicker
                slotProps={{ textField: { size: 'small' } }}
                onChange={(date: Date | null) => {
                  handleSearchDateChange(date);
                }}
              />
            </LocalizationProvider>
          </Grid> 

          {/* 検索ボタン */}
          <Grid item xs={WIDTH}>
            <Button 
              variant="contained" onClick={searchButtonClick}>
              検索
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <>
      <Box>
        {/* ボタンエリア */}
        {(isSearch) ? <>
          <ButtonArea pageName={PAGE_NAME}>
            <Button variant="contained" onClick={newButtonClick}>
              新規追加
            </Button>
          </ButtonArea>
        </> : <></>}
        <Box sx={{ pt: isSearch ? 4 : 0 }}>
          {/* 検索フォーム */}
          {(isSearch) ? 
            SearchForm() : <></>
          }
          {/* 契約一覧 */}
          {contractList.length > 0 ? <>
            <ContractTable contractList={contractList} />
          </> : <>
            <Box>
              <Grid container alignItems={'center'} justifyContent={'center'}>
                {/* 契約なし */}
                <Grid item xs={12} padding={3}>
                  <Typography variant="h2" align="center" fontSize={"1.25rem"} noWrap>
                    契約情報なし
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </>}
        </Box>
      </Box>

      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />

      {/* 処理ローディング */}
      <Loading isLoading={isLoading} />
    </>
  );
};

export default ContractList;
